
/*!
 * @pendo/components 3.25.3
 * (c) 2018-present
 */

import 'lodash/isEqual';
import 'just-kebab-case';
import './labels-mixin.js';
import 'focus-visible/dist/focus-visible.js';
import { _ as _objectSpread, a as _slicedToArray } from './_rollupPluginBabelHelpers-mixin.js';
import 'lodash/kebabCase';
import 'feather-icons';
import PendoIcon from './pendo-icon.js';
import 'vue';
import 'lodash/get';
import './pendo-loading-mixin.js';
import './pendo-loading-indicator.js';
import PendoButton from './pendo-button.js';
import 'lodash/isNumber';
import { s as setStyles } from './table-auto-height-mixin.js';
import Dialog from 'element-ui/lib/dialog';
import ResizeObserver from 'resize-observer-polyfill';

const clamp = (value, min, max) => {
  if (value < min) {
    return min;
  }

  if (value > max) {
    return max;
  }

  return value;
};
const types = [{
  name: 'px',
  regexp: new RegExp('^[-+]?[0-9]*.?[0-9]+px$')
}, {
  name: '%',
  regexp: new RegExp('^[-+]?[0-9]*.?[0-9]+%$')
}, {
  name: 'px',
  regexp: new RegExp('^[-+]?[0-9]*.?[0-9]+$')
}];

const getType = value => {
  if (value === 'auto') {
    return {
      type: value,
      value: 0
    };
  }

  for (let i = 0; i < types.length; i++) {
    const type = types[i];

    if (type.regexp.test(value)) {
      return {
        type: type.name,
        value: parseFloat(value)
      };
    }
  }

  return {
    type: '',
    value
  };
};

const parseSizeType = value => {
  switch (typeof value) {
    case 'number':
      return {
        type: 'px',
        value
      };

    case 'string':
      return getType(value);

    default:
      return {
        type: '',
        value
      };
  }
};

const SCROLL_BLOCK_CLASS = 'pendo-components--is-blocking-scroll';
var script = {
  name: 'PendoModal',
  components: {
    ElDialog: Dialog,
    PendoButton,
    PendoIcon
  },
  inheritAttrs: false,
  props: {
    isVisible: {
      type: Boolean,
      default: null
    },
    visible: {
      type: Boolean,
      default: null
    },
    callCloseModal: {
      type: Boolean,
      default: false
    },
    actionButtons: {
      type: [Array, Boolean],
      default: () => []
    },
    theme: {
      type: String,
      default: 'app'
    },
    type: {
      type: String,
      default: null
    },
    title: {
      type: String,
      default: ''
    },
    message: {
      type: String,
      default: null
    },
    confirmButtonConfig: {
      type: Object,
      default: () => ({})
    },
    cancelButtonConfig: {
      type: Object,
      default: () => ({})
    },
    fullscreen: {
      type: Boolean,
      default: false
    },
    showBackdrop: {
      type: Boolean,
      default: true
    },
    modalAppendToBody: {
      type: Boolean,
      default: true
    },
    appendToBody: {
      type: Boolean,
      default: true
    },
    showClose: {
      type: Boolean,
      default: true
    },
    beforeClose: {
      type: Function,
      default: null
    },
    clickToClose: {
      type: Boolean,
      default: false
    },
    escToClose: {
      type: Boolean,
      default: false
    },
    minWidth: {
      type: Number,
      default: 200
    },
    minHeight: {
      type: Number,
      default: 0
    },
    maxWidth: {
      type: [Number, String],
      default: Infinity
    },
    maxHeight: {
      type: [Number, String],
      default: Infinity
    },
    width: {
      type: [Number, String],
      default: 400
    },
    height: {
      type: [Number, String],
      default: 400
    },
    pivotX: {
      type: Number,
      default: 0.5
    },
    pivotY: {
      type: Number,
      default: 0.5
    }
  },

  data() {
    return {
      modal: {
        width: 0,
        widthType: '%',
        height: 0,
        heightType: '%',
        renderedHeight: 0
      },
      windowDimensions: {
        width: 0,
        height: 0
      }
    };
  },

  computed: {
    confirmButtonAttrs() {
      const confirmButtonDefaults = {
        theme: 'app',
        type: 'primary',
        label: 'Confirm'
      };
      return _objectSpread({}, confirmButtonDefaults, this.confirmButtonConfig);
    },

    cancelButtonAttrs() {
      const cancelButtonDefaults = {
        theme: 'app',
        type: 'secondary',
        label: 'Cancel'
      };
      return _objectSpread({}, cancelButtonDefaults, this.cancelButtonConfig);
    },

    showCloseButton() {
      if (this.type === 'confirmation') {
        return this.$options.propsData.showClose || false;
      }

      return this.showClose;
    },

    modalVisibility() {
      return this.visible || this.isVisible;
    },

    modalClass() {
      let classes = '';

      if (this.type === 'confirmation') {
        classes += 'is-confirmation ';
      }

      if (this.showCloseButton) {
        classes += 'is-closeable ';
      }

      if (this.modalVisibility) {
        classes += 'is-open';
      }

      return classes;
    },

    isAutoHeight() {
      return this.modal.heightType === 'auto';
    },

    modalPosition() {
      const windowDimensions = this.windowDimensions,
            pivotX = this.pivotX,
            pivotY = this.pivotY,
            trueModalWidth = this.trueModalWidth,
            trueModalHeight = this.trueModalHeight;
      const maxLeft = windowDimensions.width - trueModalWidth;
      const minLeft = 0;
      const maxTop = windowDimensions.height - trueModalHeight;
      const minTop = 0;
      const valueLeft = pivotX * maxLeft;
      const valueTop = pivotY * maxTop;
      return {
        left: parseInt(clamp(valueLeft, minLeft, maxLeft), 10),
        top: parseInt(clamp(valueTop, minTop, maxTop), 10)
      };
    },

    trueModalWidth() {
      const windowDimensions = this.windowDimensions,
            modal = this.modal,
            maxWidth = this.maxWidth,
            minWidth = this.minWidth;
      const value = modal.widthType === '%' ? windowDimensions.width / 100 * modal.width : modal.width;
      const max = Math.min(windowDimensions.width, maxWidth);
      return clamp(value, minWidth, max);
    },

    trueModalHeight() {
      const windowDimensions = this.windowDimensions,
            modal = this.modal,
            isAutoHeight = this.isAutoHeight,
            minHeight = this.minHeight,
            maxHeight = this.maxHeight;
      const value = modal.heightType === '%' ? windowDimensions.height / 100 * modal.height : modal.height;

      if (isAutoHeight) {
        return modal.renderedHeight;
      }

      const max = Math.min(windowDimensions.height, maxHeight);
      return clamp(value, minHeight, max);
    },

    modalStyle() {
      const maxHeight = this.modal.maxHeightType === '%' ? this.windowDimensions.height / 100 * this.modal.maxHeight : this.modal.maxHeight;
      const maxWidth = this.modal.maxWidthType === '%' ? this.windowDimensions.width / 100 * this.modal.maxWidth : this.modal.maxWidth;
      return _objectSpread({
        top: `${this.modalPosition.top}px`,
        left: `${this.modalPosition.left}px`,
        width: `${this.trueModalWidth}px`,
        height: this.isAutoHeight ? 'auto' : `${this.trueModalHeight}px`
      }, maxHeight !== 0 ? {
        maxHeight
      } : undefined, maxWidth !== 0 ? {
        maxWidth
      } : undefined);
    }

  },
  watch: {
    modalStyle({
      top,
      left,
      width,
      height,
      maxHeight,
      maxWidth
    } = {}) {
      if (this.$refs.pendoModal) {
        setStyles(this.$refs.pendoModal.$refs.dialog, {
          top,
          left,
          width,
          height,
          maxHeight,
          maxWidth
        });
      }
    }

  },

  beforeMount() {
    window.addEventListener('resize', this.handleWindowResize);
    this.handleWindowResize();
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.handleWindowResize);
    document.body.classList.remove(SCROLL_BLOCK_CLASS);
    this.resizeObserver.disconnect();
  },

  mounted() {
    this.resizeObserver = new ResizeObserver(entries => {
      if (entries.length > 0) {
        const _entries = _slicedToArray(entries, 1),
              entry = _entries[0];

        this.modal.renderedHeight = entry.contentRect.height;
      }
    });
  },

  updated() {
    this.$nextTick(() => this.callCloseModal && this.handleClose());
  },

  methods: {
    setInitialSize() {
      const _parseSizeType = parseSizeType(this.width),
            width = _parseSizeType.value,
            widthType = _parseSizeType.type;

      const _parseSizeType2 = parseSizeType(this.maxWidth),
            maxWidth = _parseSizeType2.value,
            maxWidthType = _parseSizeType2.type;

      let _parseSizeType3 = parseSizeType(this.height),
          height = _parseSizeType3.value,
          heightType = _parseSizeType3.type;

      const _parseSizeType4 = parseSizeType(this.maxHeight),
            maxHeight = _parseSizeType4.value,
            maxHeightType = _parseSizeType4.type;

      if (this.type === 'confirmation' && !this.$options.propsData.height) {
        height = 'auto';
        heightType = 'auto';
      }

      const renderedHeight = this.$refs.pendoModal.$refs.dialog.clientHeight;
      this.modal = {
        renderedHeight,
        width,
        widthType,
        maxWidth,
        maxWidthType,
        height,
        heightType,
        maxHeight,
        maxHeightType
      };
    },

    handleWindowResize() {
      this.windowDimensions.width = window.innerWidth;
      this.windowDimensions.height = window.innerHeight;
    },

    handleOpen() {
      document.body.classList.add(SCROLL_BLOCK_CLASS);
      this.$emit('open');
      this.$emit('update:visible', true);
      this.$nextTick(() => {
        this.resizeObserver.observe(this.$refs.pendoModal.$refs.dialog);
      });
    },

    handleOpened() {
      this.$emit('opened');
    },

    handleClose() {
      document.body.classList.remove(SCROLL_BLOCK_CLASS);
      this.resizeObserver.unobserve(this.$refs.pendoModal.$refs.dialog);
      this.$emit('modalClosed');

      if (this.modalVisibility) {
        this.$emit('close');
        this.$emit('update:visible', false);
      }
    },

    handleClosed() {
      this.$emit('closed');
    },

    handleClick(index, event, source = 'click') {
      const button = this.actionButtons[index];

      if (button && typeof button.handler === 'function') {
        button.handler(index, event, {
          source
        });
      } else {
        this.handleClose();
      }
    }

  }
};

const __vue_script__ = script;

var __vue_render__ = function () {
  var _vm = this;

  var _h = _vm.$createElement;

  var _c = _vm._self._c || _h;

  return _c('el-dialog', {
    ref: "pendoModal",
    staticClass: "pendo-modal",
    attrs: {
      "visible": _vm.modalVisibility,
      "fullscreen": _vm.fullscreen,
      "modal": _vm.showBackdrop,
      "modal-append-to-body": _vm.modalAppendToBody,
      "append-to-body": _vm.appendToBody,
      "custom-class": _vm.modalClass,
      "close-on-click-modal": _vm.clickToClose,
      "close-on-press-escape": _vm.escToClose,
      "show-close": false,
      "before-close": _vm.beforeClose,
      "top": "null"
    },
    on: {
      "hook:mounted": _vm.setInitialSize,
      "open": _vm.handleOpen,
      "opened": _vm.handleOpened,
      "close": _vm.handleClose,
      "closed": _vm.handleClosed
    }
  }, [_c('template', {
    slot: "title"
  }, [_vm._t(_vm.$slots.header ? 'header' : 'title', [_c('div', {
    staticClass: "el-dialog__title"
  }, [_vm._v("\n                " + _vm._s(_vm.title) + "\n            ")])]), _vm._v(" "), _vm.showCloseButton ? _c('div', {
    staticClass: "el-dialog__close-button"
  }, [_c('pendo-icon', {
    attrs: {
      "type": "x",
      "size": "16"
    },
    on: {
      "click": _vm.handleClose
    }
  })], 1) : _vm._e()], 2), _vm._v(" "), _vm._t(_vm.$slots.body ? 'body' : 'default', [_vm._v("\n        " + _vm._s(_vm.message) + "\n    ")]), _vm._v(" "), _c('template', {
    slot: "footer"
  }, [_vm._t("footer", [!_vm.$slots.footer && _vm.actionButtons ? _c('div', {
    staticClass: "el-dialog__button-wrapper"
  }, [_vm.actionButtons.length ? _vm._l(_vm.actionButtons, function (button, index) {
    return _c('pendo-button', _vm._b({
      key: "pendo-modal-button-" + index,
      on: {
        "click": function ($event) {
          $event.stopPropagation();
          return _vm.handleClick(index, $event);
        }
      }
    }, 'pendo-button', button, false));
  }) : _vm._e(), _vm._v(" "), !_vm.actionButtons.length ? _c('pendo-button', _vm._b({
    on: {
      "click": function ($event) {
        $event.stopPropagation();
        return _vm.$emit('cancel');
      },
      "keydown": function ($event) {
        if (!$event.type.indexOf('key') && _vm._k($event.keyCode, "enter", 13, $event.key, "Enter")) {
          return null;
        }

        $event.stopPropagation();
        return _vm.$emit('cancel');
      }
    }
  }, 'pendo-button', _vm.cancelButtonAttrs, false)) : _vm._e(), _vm._v(" "), !_vm.actionButtons.length ? _c('pendo-button', _vm._b({
    ref: "confirm",
    on: {
      "click": function ($event) {
        $event.stopPropagation();
        return _vm.$emit('confirm');
      },
      "keydown": function ($event) {
        if (!$event.type.indexOf('key') && _vm._k($event.keyCode, "enter", 13, $event.key, "Enter")) {
          return null;
        }

        $event.stopPropagation();
        return _vm.$emit('confirm');
      }
    }
  }, 'pendo-button', _vm.confirmButtonAttrs, false)) : _vm._e()], 2) : _vm._e()])], 2)], 2);
};

var __vue_staticRenderFns__ = [];
const __vue_inject_styles__ = undefined;
const __vue_scope_id__ = undefined;
const __vue_is_functional_template__ = false;

function __vue_normalize__(template, style, script, scope, functional, moduleIdentifier, createInjector, createInjectorSSR) {
  const component = (typeof script === 'function' ? script.options : script) || {};
  component.__file = "pendo-modal.vue";

  if (!component.render) {
    component.render = template.render;
    component.staticRenderFns = template.staticRenderFns;
    component._compiled = true;
    if (functional) component.functional = true;
  }

  component._scopeId = scope;

  return component;
}

var PendoModal = __vue_normalize__({
  render: __vue_render__,
  staticRenderFns: __vue_staticRenderFns__
}, __vue_inject_styles__, __vue_script__, __vue_scope_id__, __vue_is_functional_template__);

export default PendoModal;
